import type { SerializedFile } from "@repo/models";

import { downloadFile, PushProcessingNotification } from "@repo/helpers";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	ButtonTypes,
	DropDownList,
	FileUploader,
} from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import LocationsApi from "../../../api/locationsApi";
import { QUERY_KEYS } from "../../../api/queries";

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function LocationScheduleImportModal({ open, onOpenChange }: Props) {
	const [importType, setImportType] = useState<string>("");
	const [month, setMonth] = useState<string>("");
	const [year, setYear] = useState<string>("");
	const [files, setFiles] = useState<SerializedFile[]>([]);
	const [importLog, setImportLog] = useState<string>("");
	const [submitting, setSubmitting] = useState(false);

	const importTypesQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.ImportTypes],
		queryFn: () => LocationsApi.getImportTypes(),
		enabled: open,
	});

	const monthsQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.ImportMonths],
		queryFn: () => LocationsApi.getMonths(),
		enabled: open,
	});

	const yearsQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.ImportYears],
		queryFn: () => LocationsApi.getYears(),
		enabled: open,
	});

	const handleSubmit = async (e?: React.FormEvent) => {
		e?.preventDefault();
		if (files.length === 0 || !importType || !month || !year) return;

		setSubmitting(true);
		const notify = PushProcessingNotification("Processing Location Schedule Import...");

		try {
			const selectedImportType = importTypesQuery.data?.find((t) => t.Id.toString() === importType);
			const importName = selectedImportType?.Name || "";
			const importMetadata = selectedImportType?.Metadata || null;

			const formData = new FormData();
			formData.append("importType", importType);
			formData.append("Month", month);
			formData.append("Year", year);

			const file = files[0];
			if (file.url) {
				const res = await fetch(file.url);
				const blob = await res.blob();
				formData.append("locationScheduleUploadFile", blob, file.name);
			}

			formData.append("importName", importName);
			if (importMetadata) {
				formData.append("importMetadata", importMetadata);
			}

			const result = await LocationsApi.processLocationScheduleImportFile(formData);
			setImportLog(result.importLog || "Import completed.");
			notify.success("Import processed successfully.");
		} catch {
			setImportLog("An error occurred during import.");
			notify.error("Failed to process import.");
		} finally {
			setSubmitting(false);
		}
	};

	const resetForm = () => {
		setImportType("");
		setMonth("");
		setYear("");
		setFiles([]);
		setImportLog("");
	};

	const handleOpenChange = (isOpen: boolean) => {
		if (!isOpen) {
			resetForm();
		}
		onOpenChange(isOpen);
	};

	const selectedImportType = importTypesQuery.data?.find((t) => t.Id.toString() === importType);
	const importTypeName = selectedImportType?.Name || "";

	const handleDownloadTemplate = useCallback(async () => {
		try {
			const blob = await LocationsApi.downloadLocationScheduleUploadTemplate(importType, year, month);
			const url = window.URL.createObjectURL(blob);
			downloadFile(url, "location-schedule-upload-template.xlsx");
		} catch (e) {
			console.error("Failed to download template", e);
		}
	}, [importType, month, year]);

	const renderBody = () => (
		<div className="flex flex-col gap-4 p-4">
			<DropDownList
				label="Type"
				values={importTypesQuery.data || []}
				valueProp="Id"
				textProp="Name"
				value={importType}
				changeHandler={(val) => setImportType(val)}
				placeholder="Select Import Type"
				fullLength
			/>

			<DropDownList
				label="Month"
				values={monthsQuery.data || []}
				valueProp="Id"
				textProp="Value"
				value={month}
				changeHandler={(val) => setMonth(val)}
				placeholder="Select Month"
				fullLength
			/>

			<DropDownList
				label="Year"
				values={yearsQuery.data || []}
				valueProp="Id"
				textProp="Value"
				value={year}
				changeHandler={(val) => setYear(val)}
				placeholder="Select Year"
				fullLength
			/>

			<FileUploader
				id="locationScheduleUploadFile"
				label="Select Import File"
				onFilesAdded={(_, newFiles) => setFiles(newFiles)}
				files={files}
				acceptFileTypes={[".xlsx"]}
				multiple={false}
				autoReplace={true}
			/>

			{importType && year && month && (
				<div className="text-center">
					<Button onClick={handleDownloadTemplate} types={[ButtonTypes.Primary]}>
						Download the {importTypeName} import template
					</Button>
				</div>
			)}

			{importLog && (
				<div className="mt-4">
					<label className="text-sm font-medium">Import Log</label>
					<textarea className="h-40 w-full rounded border p-2 font-mono text-xs" readOnly value={importLog} />
				</div>
			)}
		</div>
	);

	return (
		<AlertDialog open={open} onOpenChange={handleOpenChange}>
			<AlertDialogContent style={{ maxWidth: "500px" }}>
				<AlertDialogHeader>
					<AlertDialogTitle>Location Schedule Import</AlertDialogTitle>
				</AlertDialogHeader>

				<AlertDialogDescription asChild>{renderBody()}</AlertDialogDescription>

				<AlertDialogFooter>
					<AlertDialogAction
						onClick={handleSubmit}
						types={[ButtonTypes.Primary]}
						disabled={submitting || files.length === 0 || !importType || !month || !year}
					>
						{submitting ? "Submitting..." : "Submit"}
					</AlertDialogAction>
					<AlertDialogCancel onClick={() => handleOpenChange(false)} types={[ButtonTypes.Outline]}>
						Cancel
					</AlertDialogCancel>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
